/**
 * Mehrzeiliges Eingabefeld mit Beschriftung, Hinweis und Fehler — die
 * Schwester von `Input`, mit demselben Rahmen, Fokusring und `<label for>`.
 *
 * Kam mit dem Feedback (HM-169): Eine Fehlerbeschreibung ist kein Einzeiler,
 * und ein eigenes `<textarea>` in der App hätte Rahmen und Fokus ein zweites
 * Mal gefasst.
 */
import { useId, useState, type TextareaHTMLAttributes } from "react";
import { fontWeight } from "../tokens.js";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly label: string;
  /** Erklärung unter dem Feld. Wird vom Fehler verdrängt, wenn einer vorliegt. */
  readonly hint?: string;
  readonly error?: string;
}

export function Textarea({ label, hint, error, disabled = false, id, rows = 6, style, ...rest }: TextareaProps) {
  const [focused, setFocused] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedById = hint !== undefined || error !== undefined ? `${inputId}-note` : undefined;

  const borderColor = error
    ? "var(--hm-danger)"
    : focused
      ? "var(--hm-green-500)"
      : "var(--border-default)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      <label htmlFor={inputId} style={{ fontSize: 13, fontWeight: fontWeight.semibold, color: "var(--text-body)" }}>
        {label}
      </label>
      <textarea
        id={inputId}
        rows={rows}
        disabled={disabled}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedById}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          resize: "vertical",
          minHeight: 110,
          padding: "10px 12px",
          background: disabled ? "var(--surface-sunken)" : "var(--surface-card)",
          border: `1px solid ${borderColor}`,
          borderRadius: "var(--radius-md)",
          boxShadow: focused ? "0 0 0 var(--ring-width) var(--focus-ring)" : "var(--shadow-xs)",
          transition: "border-color .15s ease, box-shadow .15s ease",
          opacity: disabled ? 0.6 : 1,
          outline: "none",
          fontFamily: "var(--font-body)",
          fontSize: "var(--field-font-md)",
          lineHeight: 1.5,
          color: "var(--text-strong)",
        }}
        {...rest}
      />
      {(error ?? hint) !== undefined && (
        <span id={describedById} style={{ fontSize: 12, color: error ? "var(--hm-danger)" : "var(--text-muted)" }}>
          {error ?? hint}
        </span>
      )}
    </div>
  );
}
